import { HotmartRum } from '@hot-observability-js/react';
import {
  normalizeDrippingData,
  normalizeDrippingDataFromMask
} from 'helpers/Dripping';
import omit from 'lodash/omit';
import { action, extendObservable } from 'mobx';
import { SeasonService } from 'modules/content';
import {
  LIBERATION_TYPE,
  MAX_LIBERATION_DAYS
} from 'modules/content/constants';
import { FileService } from 'modules/file';
import MembershipService from 'modules/membership/MembershipService';
import { UiService } from 'modules/ui';

import ContentPagePreventLeaveService from './ContentPagePreventLeaveService';
import Store from './ContentPageStore';
import ContentService from './ContentService';
import { DrippingStore } from './dripping/stores';
import drippingPayloadUtil from './dripping/utils/drippingPayloadUtil';
import drippingResponseUtil from './dripping/utils/drippingResponseUtil';

import { uploadNotifySave } from '@hotmart/club-files-ui';
import { sanitize } from '@hotmart/devsec-toolkit';
import Request from '@hotmart/request';

/**
 * @class
 * This class manages all operations related to Page Content, including
 * API requests and store management.
 * All required data (including dependency on data from other services) and its
 * own core data fetching should be dealt with in the `init` method.
 * It current depends on:
 * - Membership data, from `MembershipService`;
 * - Season data, from `SeasonService`;
 */
class ContentPageService {
  wasInitialized = false;

  @action
  async init() {
    if (this.wasInitialized) {
      return;
    }

    await MembershipService.init();

    if (MembershipService.hasSeason) {
      await SeasonService.init();
    }

    this.wasInitialized = true;
  }

  // #region page actions
  @action
  clear() {
    Object.assign(Store, {
      page: {
        id: null,
        name: '',
        published: false,
        liberationType: null,
        liberationDays: null,
        liberationDate: null,
        hasDuration: false,
        content: '',
        durationDays: 0,
        moduleId: null,
        pageLanguage: {
          language: '',
          signLanguage: false
        }
      },
      tags: [],
      complementaryReadings: [],
      medias: [],
      attachments: [],
      academyBadgeType: null,
      hotPassValue: null,
      mediaVideoUrl: null,
      backgroundImage: null,
      snapshot: {}
    });
  }

  async contentPreview(pageId, role = 'OWNER', season = null, classHash) {
    const tab = UiService.openNewTabWithLoading();

    const page = ContentService.findPageById(pageId);

    try {
      if (!season && MembershipService.hasSeason) {
        season = SeasonService.getSelectedSeasonInfo().id;
      }

      const preview = await this.fetchPreviewURL({
        pageHash: pageId,
        season,
        role,
        classHash,
        pageType: page?.type
      });

      tab.location.href = preview.redirectUrl;
    } catch (error) {
      HotmartRum.captureException(error);
      tab.document.close();
      tab.close();
      UiService.setAlertError('content_preview');
    }
  }

  setFetchPreviewURL = (value) => {
    this.fetchPreviewURL = action(value);
  };

  fetchPreviewURL({ pageHash, season, role, classHash }) {
    return Request('preview.get', { pageHash, season, role, classHash });
  }

  @action
  async publish(
    pages,
    status = true,
    modules = null,
    showAlert = true,
    notifyByEmail = false
  ) {
    try {
      const response = await Request('page.publish', {
        modules,
        pages,
        published: status,
        notifyByEmail
      });
      const message = status ? 'publish_success' : 'unpublish_success';

      showAlert && UiService.setAlertSuccess(message);
      return response;
    } catch (e) {
      const message = status ? 'publish_fail' : 'unpublish_fail';

      UiService.setAlertError(message);
      return false;
    }
  }

  @action
  async create(payload) {
    return await Request('page.create', payload);
  }

  @action
  // eslint-disable-next-line complexity
  async save(showAlert = true) {
    try {
      const { attachments, backgroundImage, ...store } = omit(
        Store,
        'snapshot'
      );

      if (!this.drippingConfigIsValid(store.page)) {
        return false;
      }

      const mediaIdList = FileService.mapFilesId(
        FileService.orderFilesByFileOrder(this.mediaSource)
      );

      const attachmentIdList = FileService.mapFilesId(
        FileService.orderFilesByFileOrder(attachments)
      );
      const backgroundImageId = backgroundImage
        ? backgroundImage.fileId || backgroundImage.id
        : null;

      const method = this.moduleId ? 'page.create' : 'page.update';
      const payload = {
        pageId: this.pageId,
        mediaIdList,
        attachmentIdList,
        backgroundImageId,
        ...store
      };
      const pageLanguage = payload.page?.pageLanguage?.language
        ? payload.page.pageLanguage
        : null;

      payload.page = {
        ...normalizeDrippingData(payload.page),
        ...drippingPayloadUtil.getPayload(this.moduleId),
        pageLanguage
      };

      const response = await Request(method, payload);

      showAlert && UiService.setAlertSuccess('done');

      ContentPagePreventLeaveService.updateSnapshot();

      response && uploadNotifySave(response.id);

      return response;
    } catch (error) {
      HotmartRum.captureException(error);
      HotmartRum.captureMessage(`Error saving updated content data: ${error}`);
      UiService.handleError(error);
      return false;
    }
  }

  drippingConfigIsValid = (page) => {
    return !(
      page.liberationDays &&
      page.liberationType === LIBERATION_TYPE.BY_DAYS &&
      page.liberationDays > MAX_LIBERATION_DAYS
    );
  };
  // #endregion

  // #region getters
  @action
  async getCertificates() {
    try {
      const response = await Request('certificates.get');

      return response;
    } catch {
      return [];
    }
  }

  @action
  async getArticleInfo(url) {
    try {
      const response = await Request('article.get', { articleUrl: url });

      return response;
    } catch (error) {
      UiService.setAlertError('article_invalid_url');
      return false;
    }
  }

  @action
  async getTags() {
    try {
      const response = await Request('tags.suggestions');

      return response;
    } catch (error) {
      return [];
    }
  }

  @action
  async getPage(pageId) {
    const response = await Request('page.get', { pageId });
    const medias = await FileService.fetchMedias(pageId);
    const attachments = await FileService.fetchAttachments(pageId);
    const [backgroundImage] =
      (await FileService.fetchPageBackgroundImg(pageId)) || [];

    const data = {
      ...response,
      attachments: FileService.orderFilesByFileOrder(attachments),
      medias: FileService.sortMediasByOrderOrCreationDate(medias),
      backgroundImage: backgroundImage || null
    };
    const safeHTML = sanitize({ html: data.page.content });

    data.page = {
      ...normalizeDrippingDataFromMask(data.page),
      content: safeHTML
    };

    const drippings = drippingResponseUtil.getNormalizedItems(
      data.page.drippingConfigs || []
    );

    DrippingStore.setItems(drippings, false);

    // TODO: melhorar a solução do Prevent Leave da página.
    delete data.page.drippingConfigs;
    delete data.page.classesAvailableForDripping;

    this.setPageData(data);
    ContentPagePreventLeaveService.initSnapshot(data);
  }

  get moduleId() {
    return Store.page.moduleId;
  }

  set moduleId(id) {
    Store.page.moduleId = id;
  }

  get pageId() {
    return Store.page.id;
  }

  get published() {
    return Store.page.published;
  }

  set published(status) {
    Store.page.published = status;
  }

  get mediaSource() {
    const { medias } = Store;

    return medias;
  }

  // #endregion

  // #region setters
  @action
  setPageData(data) {
    extendObservable(Store, data);
  }

  set name(name) {
    Store.page.name = name;
  }

  set content(content) {
    Store.page.content = content;
  }

  set liberationDays(liberationDays) {
    Store.page.liberationDays = liberationDays;
  }

  set liberationType(liberationType) {
    Store.page.liberationType = liberationType;
  }

  set hasDuration(hasDuration) {
    Store.page.hasDuration = hasDuration;
  }

  set liberationDate(liberationDate) {
    Store.page.liberationDate = liberationDate;
  }

  set durationDays(durationDays) {
    Store.page.durationDays = durationDays;
  }

  set tags(tags) {
    Store.tags = tags;
  }

  set language(language) {
    Store.page.pageLanguage = {
      ...Store.page.pageLanguage,
      language
    };
  }

  set signLanguage(signLanguage) {
    Store.page.pageLanguage = {
      ...Store.page.pageLanguage,
      signLanguage: Boolean(signLanguage)
    };
  }

  set complementaryReadings(articles) {
    Store.complementaryReadings = articles;
  }

  set hotPassValue(value) {
    Store.hotPassValue = value;
  }

  set academyBadgeType(value) {
    Store.academyBadgeType = value;
  }

  set mediaVideoUrl(value) {
    Store.mediaVideoUrl = value;
  }

  set medias(value) {
    Store.medias = value;
  }

  set attachments(value) {
    Store.attachments = value;
  }

  set backgroundImage(value) {
    Store.backgroundImage = value;
  }
  // #endregion
}

export default new ContentPageService();
